// react
import { Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import {
	Document,
	DocumentType,
	Entity,
	ErrorCause,
	ExpenseType,
	Feedback,
	FilePayload,
	Transaction,
	TransactionType,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid } from '@mui/material';

// fields
import { FilePicker } from '../../fields/FilePicker/FilePicker';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';
import { DocsTraceability } from '../../ui/DocsTraceability/DocsTraceability';

// utils
import { toKebabCase } from '../../../utils/strings';
import { translateError } from '../../../utils/translations';

// types
export type FormUploadDocsResource = 'ACT' | 'CONTRACT' | 'ENTITY' | 'TRANSACTION';

// props
export interface FormUploadDocsProps {
	actId?: number;
	contractId?: number;
	entity?: Entity;
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	transaction?: Transaction;
}

export const FormUploadDocs = ({
	actId,
	contractId,
	entity,
	onClose,
	onSuccess,
	setIsLoading,
	transaction,
}: FormUploadDocsProps) => {
	// hooks
	const { t } = useTranslation();

	// derive resource type from props
	let resource: FormUploadDocsResource = 'ACT';

	if (entity) {
		resource = 'ENTITY';
	} else if (contractId) {
		resource = 'CONTRACT';
	} else if (transaction) {
		resource = 'TRANSACTION';
	}

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
	const [docTypesSelected, setDocTypesSelected] = useState<string[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [files, setFiles] = useState<File[]>([]);

	// vars
	const idForm = `form-upload-docs-${resource.toLowerCase()}`;
	const expenseType = transaction?.expenseType as ExpenseType;

	// fetch document types for transaction expense type
	let urlFetchExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types/${expenseType?.id}?populate=documentTypes`;
	let paramEntity = 'null';

	if (userEntity) {
		paramEntity += `,${userEntity.id}`;
	}

	urlFetchExpenseTypes += `&entity=${paramEntity}`;

	useFetch({
		isEnabled: Boolean(resource === 'TRANSACTION' && expenseType),
		url: urlFetchExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocTypes(res.data[0].documentTypes);
			}
		},
	});

	// fetch document types for other resources
	useFetch({
		isEnabled: Boolean(resource !== 'TRANSACTION'),
		url: `${process.env.REACT_APP_API_URL}/document-types?entity=${paramEntity}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocTypes(res.data);
			}
		},
	});

	// fetch documents for transaction
	useFetch({
		isEnabled: Boolean(resource === 'TRANSACTION' && transaction),
		url: `${process.env.REACT_APP_API_URL}/documents?transaction=${transaction?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}
		},
	});

	// calculate doc types
	const docTypesExisting = documents.map((doc) => String(doc.type));
	const docTypesChecked = [...new Set(docTypesExisting.concat(docTypesSelected))];

	// determine if traceability is needed
	const hasTraceability =
		resource === 'TRANSACTION' &&
		['CASH_BENEFIT', 'OPEX', 'PAYMENT'].includes(transaction?.type || '');

	// options for document types
	const optionsDocTypes = docTypes.map((docType) => {
		return {
			label: docType.name,
			value: String(docType.id),
		};
	});

	// build path and form data based on resource type
	const buildFormData = (file: File, docType: string | null): FormData => {
		const formData = new FormData();
		const dateToday = new Date();
		const year = dateToday.getFullYear();
		const month = new Intl.DateTimeFormat('en-US', {
			month: '2-digit',
		}).format(dateToday);

		let path = '';

		switch (resource) {
			case 'ACT':
				if (actId) {
					path = `bldt/projects/${project?.id}/acts/${actId}/${year}/${month}/`;
					formData.append('act', String(actId));
				}
				break;

			case 'CONTRACT':
				if (contractId) {
					path = `bldt/projects/${project?.id}/contract/${contractId}/${year}/${month}/`;
					formData.append('contract', String(contractId));
				}
				break;

			case 'ENTITY':
				if (entity) {
					let directory = 'entities';

					if (entity.type === 'BENEFICIARY') {
						directory = 'beneficiaries';
					} else if (entity.type === 'SUPPLIER') {
						directory = 'suppliers';
					}

					path = `bldt/projects/${project?.id}/${directory}/${entity.id}/${year}/${month}/`;
					formData.append('entities[]', String(entity.id));
				}
				break;

			case 'TRANSACTION':
				if (transaction) {
					const typePaths: Record<TransactionType, string> = {
						ADVANCE: 'advances',
						CASH_BENEFIT: 'cash-benefits',
						DIRECT: 'direct',
						INITIAL: 'initial-deposits',
						NON_CASH_BENEFIT: 'non-cash-benefits',
						OPEX: 'opex',
						PAYMENT: `payments/${toKebabCase(String(expenseType?.name))}`,
						PAYMENT_REFUND: 'payment-refunds',
						REIMBURSEMENT: 'reimbursements',
						REPLENISHMENT: 'replenishments',
						SPECIAL: 'special',
						TRANSFER: 'transfers',
					};

					path = `bldt/projects/${project?.id}/transactions/${typePaths[transaction.type]}/${
						transaction.id
					}/${year}/${month}/`;
					formData.append('transaction', String(transaction.id));
				}
				break;
		}

		if (docType) {
			formData.append('type', docType);
		}

		formData.append('path', path);
		formData.append('file', file);

		if (project) {
			formData.append('project', String(project.id));
		}

		return formData;
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setFeedback(null);

			// set loader
			setIsLoading(true);

			const documentsToUpload: FilePayload[] = [];

			// define documents using files
			if (files.length) {
				for (let i = 0; i < files.length; i++) {
					// add document
					documentsToUpload.push({
						file: files[i],
						type: entries[`file-${i}-doc-type`] ? String(entries[`file-${i}-doc-type`]) : null,
					});
				}
			}

			// for each document
			for (let i = 0; i < documentsToUpload.length; i++) {
				// define doc
				const doc = documentsToUpload[i];

				// build form data
				const formData = buildFormData(doc.file, doc.type);

				// POST document
				const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
					method: 'post',
					body: formData,
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					throw new Error(resPostDoc.error.message, {
						cause: {
							id: resPostDoc.error.id,
						},
					});
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.documentsUploaded'),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			setFeedback({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	const resourceFormNames: Record<FormUploadDocsResource, string> = {
		ACT: 'formUploadActDocuments',
		CONTRACT: 'formUploadContractDocuments',
		ENTITY: 'formUploadEntityDocuments',
		TRANSACTION: 'formUploadTransactionDocuments',
	};

	return (
		<form id={idForm} name={resourceFormNames[resource]} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							if (hasTraceability) {
								if (optionsDocTypes.length) {
									const defaultDocType = optionsDocTypes[0].value as string;

									if (!docTypesSelected.includes(defaultDocType)) {
										setDocTypesSelected([...docTypesSelected, defaultDocType]);
									}
								}
							}

							setFiles(filesSelected);
						}}
						onChangeDocType={(e, i) => {
							if (hasTraceability) {
								// clone doc types selected
								const docTypesSelectedNew = [...docTypesSelected];

								// define new doc type
								const docTypeNew = String(e.target.value);

								// replace or add doc type
								if (docTypesSelectedNew[i] !== undefined) {
									docTypesSelectedNew[i] = docTypeNew;
								} else {
									docTypesSelectedNew.push(docTypeNew);
								}

								setDocTypesSelected(docTypesSelectedNew);
							}
						}}
						onRemove={(e, i) => {
							// clone doc types selected
							const docTypesSelectedNew = [...docTypesSelected];

							// remove selected doc by index
							docTypesSelectedNew.splice(i, 1);

							setDocTypesSelected(docTypesSelectedNew);
						}}
						optionsDocTypes={optionsDocTypes}
						required={true}
					/>
				</Grid>
				{hasTraceability && (
					<Grid item xs={12}>
						<DocsTraceability docTypes={docTypes} docTypesChecked={docTypesChecked} />
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
